import { useExternalIngestionFolderListForOrganizationQuery } from '@client/graphql/__generated__/main-operations';
import {
  ExternalIngestionFolderFragment,
  TransactionPropertyType,
  TransactionSide,
  UsState,
} from '@client/graphql/__generated__/types';
import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { useInfiniteQuery } from '~/services/document/hooks';
import { useAppContext } from '~/services/main/contexts/AppContext';

export enum ComplianceTab {
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  ALL_FOLDERS = 'ALL_FOLDERS',
}

interface OrganizationCompliancePageContextType {
  // Query state
  folders: ExternalIngestionFolderFragment[];
  loading: boolean;
  error: Error | null | undefined;
  fetchTriggerRef: (ref: HTMLDivElement | null) => void;

  // Tab state
  selectedTab: ComplianceTab;
  setSelectedTab: (tab: ComplianceTab) => void;

  // Filter state
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  agentSearchTerm: string;
  setAgentSearchTerm: (term: string) => void;
  officeFilter: string | null;
  setOfficeFilter: (office: string | null) => void;
  statusFilter: string | null;
  setStatusFilter: (status: string | null) => void;
  sideFilter: TransactionSide | 'ALL';
  setSideFilter: (side: TransactionSide | 'ALL') => void;
  stateFilter: UsState | null;
  setStateFilter: (state: UsState | null) => void;
  propertyTypeFilter: TransactionPropertyType | null;
  setPropertyTypeFilter: (propertyType: TransactionPropertyType | null) => void;
  dateRangeStart: Date | null;
  setDateRangeStart: (date: Date | null) => void;
  dateRangeEnd: Date | null;
  setDateRangeEnd: (date: Date | null) => void;
  hasActiveFilters: boolean;

  // Organization data
  organizationId: string | null;
}

const OrganizationCompliancePageContext = createContext<
  OrganizationCompliancePageContextType | undefined
>(undefined);

interface OrganizationCompliancePageProviderProps {
  children: ReactNode;
}

export const OrganizationCompliancePageProvider: FC<
  OrganizationCompliancePageProviderProps
> = ({ children }) => {
  const { selectedOrganization } = useAppContext();
  const organizationId = selectedOrganization?.id || null;

  // Tab state - default to NEEDS_REVIEW
  const [selectedTab, setSelectedTab] = useState<ComplianceTab>(
    ComplianceTab.NEEDS_REVIEW
  );

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [officeFilter, setOfficeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sideFilter, setSideFilter] = useState<TransactionSide | 'ALL'>('ALL');
  const [stateFilter, setStateFilter] = useState<UsState | null>(null);
  const [propertyTypeFilter, setPropertyTypeFilter] =
    useState<TransactionPropertyType | null>(
      TransactionPropertyType.RESIDENTIAL
    );
  // Date range filter - default to last two weeks
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);
  // Query with filters
  const [_fetchTriggerRef, { data, loading, error }] = useInfiniteQuery(
    useExternalIngestionFolderListForOrganizationQuery,
    {
      variables: {
        input: {
          organizationId: organizationId!,
          // Include search term if provided
          ...(searchTerm && { search: searchTerm }),
          // Include agent search term if provided
          ...(agentSearchTerm && { agentSearch: agentSearchTerm }),
          // Include office filter if provided
          ...(officeFilter && { office: officeFilter }),
          // Include status filter if provided
          ...(statusFilter && { status: statusFilter }),
          // Include transactionSide filter if not 'ALL'
          ...(sideFilter !== 'ALL' && { transactionSide: sideFilter }),
          // Include state filter if provided
          ...(stateFilter && { state: stateFilter }),
          // Include propertyType filter if provided
          ...(propertyTypeFilter && { propertyType: propertyTypeFilter }),
          // Include date range filters if provided
          ...(dateRangeStart && { updatedAtStart: dateRangeStart }),
          ...(dateRangeEnd && { updatedAtEnd: dateRangeEnd }),
          // Include needsReview filter based on selected tab
          needsReview: selectedTab === ComplianceTab.NEEDS_REVIEW,
        },
        // NOTE(mike): As of 2025-07-15, to specify ordering by the `folderUpdatedAt` field,
        // useExternalIngestionFolderListForOrganizationQuery must also request the `folderUpdatedAt`
        // field
        orderBy: '-folderUpdatedAt',
        perPage: 20,
      },
      skip: !organizationId,
    }
  );

  const folders =
    data?.externalIngestionFolderListForOrganization?.results || [];

  // Create a wrapper function that handles null refs
  const fetchTriggerRef = (ref: HTMLDivElement | null) => {
    if (ref) {
      _fetchTriggerRef(ref);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    searchTerm ||
      agentSearchTerm ||
      officeFilter ||
      statusFilter ||
      sideFilter !== 'ALL' ||
      stateFilter ||
      propertyTypeFilter
  );

  const contextValue: OrganizationCompliancePageContextType = {
    folders,
    loading,
    error,
    fetchTriggerRef,
    selectedTab,
    setSelectedTab,
    searchTerm,
    setSearchTerm,
    agentSearchTerm,
    setAgentSearchTerm,
    officeFilter,
    setOfficeFilter,
    statusFilter,
    setStatusFilter,
    sideFilter,
    setSideFilter,
    stateFilter,
    setStateFilter,
    propertyTypeFilter,
    setPropertyTypeFilter,
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,
    hasActiveFilters,
    organizationId,
  };

  return (
    <OrganizationCompliancePageContext.Provider value={contextValue}>
      {children}
    </OrganizationCompliancePageContext.Provider>
  );
};

export const useOrganizationCompliancePage = () => {
  const context = useContext(OrganizationCompliancePageContext);

  if (!context) {
    throw new Error(
      'useOrganizationCompliancePage must be used within an OrganizationCompliancePageProvider'
    );
  }

  return context;
};
